<template>
  <div class="add-post">
    <h1>Edytuj wpisy</h1>
    <el-form ref="post" :model="post" label-width="160px" class="add-post-form">
      <el-form-item
        label="Tytuł"
        prop="title"
        :rules="[
          { required: true, message: 'Tytuł jest wymagany'},
        ]"
      >
        <el-input v-model="post.title" type="title" autocomplete="off" />
      </el-form-item>
      <el-form-item
        label="Zdjęcie"
        prop="image"
      >
        <img v-if="activePhotoPreview && mainPhotoUrl" :src="mainPhotoUrl" alt="" class="post-photo">
        <el-upload
          class="upload"
          list-type="picture-card"
          :on-preview="handlePreview"
          :on-remove="handleRemove"
          :on-change="handleChangeImages"
          :auto-upload="false"
          :limit="1"
          :file-list="fileList"
        >
          <el-button size="small" type="primary">Zmień zdjęcie</el-button>
          <div slot="tip" class="el-upload__tip">jpg/png zdjęcia nie większe niż 5 MB</div>
        </el-upload>
      </el-form-item>
      <el-form-item
        label="Treść"
        prop="content"
        :rules="[
          { required: true, message: 'Treść jest wymagana'},
        ]"
      >
        <vue-editor v-model="post.content" use-custom-image-handler :editor-options="editorSettings" @image-added="handleImageAdded" />
      </el-form-item>

      <el-divider content-position="left">SEO i Open Graph</el-divider>

      <el-form-item label="Meta title">
        <el-input v-model="post.meta_title" placeholder="Domyślnie = tytuł" maxlength="255" show-word-limit />
      </el-form-item>

      <el-form-item label="Meta description">
        <el-input v-model="post.meta_description" type="textarea" :rows="3" maxlength="500" show-word-limit />
      </el-form-item>

      <el-form-item label="Meta keywords">
        <el-input v-model="post.meta_keywords" maxlength="500" show-word-limit placeholder="słowo1, słowo2, słowo3" />
      </el-form-item>

      <el-form-item label="OG image">
        <div v-if="currentOgImageUrl && !replacingOgImage" class="og-image-preview">
          <img :src="currentOgImageUrl" alt="OG image">
          <el-button size="small" type="text" @click="replacingOgImage = true">Zmień obraz</el-button>
        </div>
        <el-upload
          v-else
          :on-change="handleOgImageChange"
          :on-remove="handleOgImageRemove"
          :auto-upload="false"
          :limit="1"
          :file-list="ogImageList"
          accept="image/*"
        >
          <el-button size="small" type="primary">Wybierz obraz</el-button>
          <div slot="tip" class="el-upload__tip">JPG/PNG/WEBP, max 2MB. Zalecane 1200x630px.</div>
        </el-upload>
      </el-form-item>

      <el-divider content-position="left">Publikacja</el-divider>

      <el-form-item label="Status">
        <el-select v-model="post.status">
          <el-option label="Szkic" value="draft" />
          <el-option label="Zaplanowany" value="scheduled" />
          <el-option label="Opublikowany" value="active" />
          <el-option label="Nieaktywny" value="in_active" />
        </el-select>
      </el-form-item>

      <el-form-item
        v-if="post.status === 'scheduled'"
        label="Data publikacji"
        prop="scheduled_at"
        :rules="[{ required: true, message: 'Data publikacji jest wymagana dla zaplanowanego posta' }]"
      >
        <el-date-picker
          v-model="post.scheduled_at"
          type="datetime"
          format="dd.MM.yyyy HH:mm"
          value-format="yyyy-MM-dd HH:mm:ss"
          :picker-options="datePickerOptions"
          placeholder="Wybierz datę i godzinę publikacji"
        />
        <div class="el-form-item__tip">Post pojawi się publicznie automatycznie (cron sprawdza co 10 min).</div>
      </el-form-item>

      <el-form-item>
        <el-button type="primary" @click="submitForm()">Zapisz</el-button>
        <el-button v-if="post.status === 'scheduled'" type="success" @click="publishNow()">
          Opublikuj teraz
        </el-button>
        <el-button v-if="post.status === 'active'" type="warning" @click="moveToDraft()">
          Cofnij do szkicu
        </el-button>
      </el-form-item>
    </el-form>
    <AddHtmlDialog
      v-model="htmlContent"
      :dialog-visible="insertDialog"
      @dialogContentChanged="updateValHtml"
      @close="insertDialog = false"
    />
  </div>
</template>

<script>
import { VueEditor, Quill } from 'vue2-editor';
import ImageResize from 'quill-image-resize-vue';
import { ImageDrop } from 'quill-image-drop-module';
import PostResource from '@/api/post';
import AddHtmlDialog from '@/views/newsletter/AddHtmlDialog';

const postResource = new PostResource();

Quill.register('modules/imageDrop', ImageDrop);
Quill.register('modules/imageResize', ImageResize);

// Białolista pól ładowanych z backendu do formularza. Wcześniej spread `{...post}`
// wciągał cały graf z PostResource (user object, user_id, timestamps, deleted_at, ...)
// — formularz odsyłał potem te nadmiarowe pola i polował na bug 'image' vs 'main_photo'.
const FORM_FIELDS = [
  'title',
  'content',
  'status',
  'scheduled_at',
  'meta_title',
  'meta_description',
  'meta_keywords',
];

export default {
  name: 'EditPost',
  components: {
    VueEditor,
    AddHtmlDialog,
  },
  data() {
    return {
      activePhotoPreview: true,
      // Lokalna kopia URL-i, których nie odsyłamy na backend — używane tylko do preview.
      mainPhotoUrl: null,
      ogImageUrl: null,
      post: {
        title: '',
        content: '',
        status: 'active',
        scheduled_at: null,
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
      },
      mainPhotoFile: null,
      fileList: [],
      ogImageFile: null,
      ogImageList: [],
      replacingOgImage: false,
      previewDialogVisible: false,
      previewDialogUrl: '',
      htmlContent: '',
      insertDialog: false,
      // Po inicjalnym setUp w getPost() watcher na status czyściłby scheduled_at,
      // gubiąc datę z bazy gdy admin tylko otworzył edycję i nic nie tknął.
      initialized: false,
      datePickerOptions: {
        disabledDate(date) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return date < today;
        },
      },
      editorSettings: {
        modules: {
          imageDrop: true,
          imageResize: {},
          toolbar: {
            container: [
              ['bold', 'italic', 'underline', 'strike'],
              [{ header: [false, 1, 2, 3, 4, 5, 6] }],
              [
                { align: '' },
                { align: 'center' },
                { align: 'right' },
                { align: 'justify' },
              ],
              ['blockquote', 'code-block'],
              [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
              [{ script: 'sub' }, { script: 'super' }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ color: [] }, { background: [] }],
              [{ direction: 'rtl' }],
              ['image'],
              [{ undo: 'undo' }],
            ],
            handlers: {
              undo: () => {
                this.openInsertDialog();
              },
            },
          },
        },
      },
    };
  },
  computed: {
    currentOgImageUrl() {
      return this.ogImageUrl;
    },
  },
  watch: {
    'post.status'(newStatus) {
      // Nie czyścimy w trakcie inicjalnego ładowania — inaczej data z bazy zniknie
      // po pierwszym hydratacji `post`.
      if (!this.initialized) {
        return;
      }
      if (newStatus !== 'scheduled') {
        this.post.scheduled_at = null;
      }
    },
  },
  mounted() {
    this.getPost();
  },
  methods: {
    async getPost() {
      const slug = this.$route.query.post;
      if (!slug) {
        this.$message.error('Brak identyfikatora posta w adresie URL.');
        this.$router.push({ path: '/blog/posts-list' });
        return;
      }

      const result = await postResource.get(slug);
      const data = result && result.data ? result.data : result;

      // Whitelist — kopiujemy tylko pola edytowalne, normalizując null→'' dla
      // pól tekstowych (żeby el-input nie krzyczał o `value` undefined).
      const form = {};
      for (const field of FORM_FIELDS) {
        const value = data[field];
        if (field === 'scheduled_at') {
          form[field] = value || null;
        } else if (field === 'status') {
          form[field] = value || 'active';
        } else {
          form[field] = value == null ? '' : value;
        }
      }
      this.post = form;
      this.mainPhotoUrl = data.main_photo_url || null;
      this.ogImageUrl = data.og_image_url || null;

      // Dopiero teraz pozwalamy watcherowi reagować na zmiany.
      this.$nextTick(() => {
        this.initialized = true;
      });
    },
    submitForm() {
      this.$refs.post.validate(async(valid) => {
        if (!valid) {
          this.$notify({
            title: 'Error',
            message: 'Popraw formularz',
            type: 'error',
            duration: 2000,
          });
          return false;
        }
        await this.send();
      });
    },
    async publishNow() {
      this.post.status = 'active';
      this.post.scheduled_at = null;
      await this.send();
    },
    async moveToDraft() {
      this.post.status = 'draft';
      this.post.scheduled_at = null;
      await this.send();
    },
    async send() {
      const formData = new FormData();
      formData.append('title', this.post.title || '');
      formData.append('content', this.post.content || '');
      formData.append('status', this.post.status);
      // Wysyłamy scheduled_at tylko gdy faktycznie zaplanowane — inaczej pomijamy
      // klucz, żeby Laravel `nullable|date` nie próbował parsować pustego stringa.
      if (this.post.status === 'scheduled' && this.post.scheduled_at) {
        formData.append('scheduled_at', this.post.scheduled_at);
      }
      formData.append('meta_title', this.post.meta_title || '');
      formData.append('meta_description', this.post.meta_description || '');
      formData.append('meta_keywords', this.post.meta_keywords || '');
      if (this.mainPhotoFile) {
        formData.append('main_photo', this.mainPhotoFile);
      }
      if (this.ogImageFile) {
        formData.append('og_image', this.ogImageFile);
      }
      await postResource.update(this.$route.query.post, formData);
      await this.$router.push({ path: '/blog/posts-list' });
    },
    handlePreview(file) {
      this.previewDialogUrl = file.url;
      this.previewDialogVisible = true;
    },
    handleChangeImages(file) {
      this.activePhotoPreview = false;
      this.mainPhotoFile = file.raw;
    },
    async handleRemove() {
      this.mainPhotoFile = null;
    },
    handleOgImageChange(file) {
      this.ogImageFile = file.raw;
      this.ogImageList = [file];
    },
    handleOgImageRemove() {
      this.ogImageFile = null;
      this.ogImageList = [];
    },
    openInsertDialog() {
      this.insertDialog = true;
    },
    updateValHtml(value) {
      this.htmlContent = value;
      this.post.content = this.htmlContent;
    },
    async handleImageAdded(file, Editor, cursorLocation, resetUploader) {
      const formData = new FormData();
      formData.append('image', file);
      const result = await postResource.imgStore(formData);
      Editor.insertEmbed(cursorLocation, 'image', result);
      resetUploader();
    },
  },
};
</script>

<style lang="scss" scoped>
  .add-post {
    margin: 0 30px;
    .add-post-form {
      .post-photo {
        height: 140px;
      }
    }
    .og-image-preview {
      img {
        max-width: 320px;
        max-height: 200px;
        display: block;
        margin-bottom: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
      }
    }
    .el-form-item__tip {
      font-size: 12px;
      color: #909399;
      margin-top: 4px;
    }
  }
</style>
